import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import { ReactComponent as CheckIcon } from '../../../../../../../assets/icons/check.svg';
import Table from '../../../../../../_common/Tables/Table';
import { Card, SectionHead, StatsCards } from '../../PipelineComponents';
import { formattedCount } from '../../../pipelineHelpers';
import GapDaysDistributionCard from './GapDaysDistributionCard';
import GapDaysMembersCard from './GapDaysMembersCard';

const byClassHeader = [
  { name: 'Class', path: 'brand' },
  { name: 'Members', path: 'members' },
  { name: 'Fill-to-fill', path: 'avgFillToFill' },
  { name: 'Cumulative', path: 'avgCumulativeGap' },
];

const GapDays = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [gapDaysData, setGapDaysData] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getGapDaysSummary(params);

        setGapDaysData(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const cards = useMemo(() => {
    const stats = gapDaysData?.kpis;

    return [
      {
        label: 'Members measured',
        value: formattedCount(stats?.membersMeasured),
        tooltip: {
          text: 'Everyone at your hub who picked up at least one prescription in the selected dates.',
          steps: [
            'Each person counts once, however many prescriptions they filled.',
            'Members with no gap at all are still counted here.',
          ],
          note: 'This is the group every other number on the page is measured against.'
            + ' Widen the date range and it grows.',
        },
      },
      {
        label: 'Members with any gap',
        value: formattedCount(stats?.membersWithGap),
        tooltip: {
          text: 'Members who went at least one day without their medication in this period.',
          steps: [
            'Each member counts once, however many times they fell behind.',
            'Members who never ran out are left out.',
          ],
          note: 'A single late day puts someone here, so this counts more people than are genuinely at risk.'
            + ' The chart below splits them by how far behind they fell.',
        },
      },
      {
        label: 'Avg cumulative gap days',
        value: stats?.avgCumulativeGap,
        tooltip: {
          text: 'How long the average member went without medication in this period.',
          steps: [
            'Spread across everyone measured, and most members had no gap at all.',
            'Among the members who did fall behind, the average is far higher.',
          ],
          note: 'The headline looks small because most members are fine. Use it to watch the trend month to'
            + ' month, not to judge any one case.',
        },
      },
      {
        label: 'Avg fill-to-fill gap',
        value: stats?.avgFillToFill,
        tooltip: {
          text: 'The average wait between one supply running out and the next pickup.',
          steps: [
            'Measured at each refill, not once per member.',
            'A member\'s first fill of a medication has nothing before it, so it is not counted.',
            'Picking up early counts as no gap — it never earns credit.',
          ],
          note: 'A member with several short delays looks fine here and still shows a large cumulative total.'
            + ' Read the two together.',
        },
      },
      {
        label: 'Gap ≥ 30 days',
        value: formattedCount(stats?.gapOver30),
        tooltip: {
          text: 'Members who missed a month or more of medication in total.',
          steps: [
            'It is the running total, so three separate ten-day delays count the same as one long one.',
            'Covers the 30–59 day and 60+ day bands in the chart below.',
          ],
          note: 'This is the list to work first. A reminder call alone is unlikely to be enough for anyone in it.',
        },
      },
      {
        label: 'Flagged to pipeline today',
        value: formattedCount(stats?.flaggedToday),
        tooltip: {
          text: 'Members added to the outreach pipeline today because their supply ran out.',
          steps: [
            'Members get 7 days grace after their supply ends before anything is flagged.',
            'Anyone already in the pipeline is skipped, so nobody is flagged twice.',
            'Counts today only — it starts again at midnight.',
          ],
          note: 'Zero here alongside members already a month behind is normal. Those were flagged on earlier'
            + ' days and are already sitting in section 03.',
        },
      },
    ];
  }, [gapDaysData]);

  return (
    <>
      <SectionHead section={section} />

      <StatsCards
        firstLoad={firstLoad.current}
        loading={loading}
        cards={cards}
      />

      <div className={`${classes.row} ${classes.row_3}`}>
        <GapDaysDistributionCard
          params={params}
          buckets={gapDaysData?.distribution}
          loading={loading}
          firstLoad={firstLoad.current}
        />

        <Card
          title="How each measure is calculated"
          caption="Both come from the same Scripts fields"
        >
          <div className={`${classes.defs} ${classes.bordered}`}>
            <div className={classes.def}>
              <p className={classes.def_title}>Fill-to-fill gap</p>

              <p className={classes.def_text}>
                Days between the end of the last days supply and the next fill date, for one member
                and one drug. Measured per fill event - shows
                {' '}
                <i>where</i>
                {' '}
                the member fell behind.
              </p>
            </div>

            <div className={classes.def}>
              <p className={classes.def_title}>Cumulative gap days</p>

              <p className={classes.def_text}>
                Sum of every fill-to-fill gap across the measurement period, plus days from the last
                supply end to today if still unfilled. Shows
                {' '}
                <i>how much</i>
                {' '}
                therapy was missed overall.
              </p>
            </div>
          </div>

          <div className={classes.kv_list}>
            <div className={classes.kv}>
              Source fields

              <span className={classes.kv_value}>
                <span className={`${classes.badge} ${classes.badge_have}`}>already in Scripts</span>
              </span>
            </div>

            <div className={classes.kv}>
              billOnDate / dispensing date

              <CheckIcon />
            </div>

            <div className={classes.kv}>
              daysSupply

              <CheckIcon />
            </div>

            <div className={classes.kv}>
              PatientId + drug / NDC

              <CheckIcon />
            </div>
          </div>
        </Card>

        <Card
          title="Gap days by drug class"
          caption="Where the therapy loss concentrates"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!gapDaysData?.byDrugClass?.length}
        >
          <div className={classes.pipeline_table}>
            <Table
              header={byClassHeader}
              data={gapDaysData?.byDrugClass || []}
            />
          </div>
        </Card>
      </div>

      <GapDaysMembersCard params={params} />
    </>
  );
};

GapDays.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default GapDays;
